import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import {
  buildDefaultFilters,
  buildReportParams,
  getRangeByPreset,
  currency,
  ReportFilters,
} from "./filterUtils";
import ReportsFilters from "../../../components/reports/ReportsFilters";
import DownloadCsvButton from "../../../components/reports/DownloadCsvButton";
import { buildCsvFromSections, downloadCsv } from "../../../utils/csvExport";

interface TaxBreakdownRow {
  taxName: string;
  taxableAmount: number;
  taxableItemSales: number;
  taxableServiceCharges: number;
  taxCollected: number;
}

interface TaxReportResponse {
  taxableItemSales: number;
  taxableServiceCharges: number;
  nonTaxableItemSales: number;
  nonTaxableServiceCharges: number;
  taxBreakdown: TaxBreakdownRow[];
  totalTaxCollected: number;
}

const initialData: TaxReportResponse = {
  taxableItemSales: 0,
  taxableServiceCharges: 0,
  nonTaxableItemSales: 0,
  nonTaxableServiceCharges: 0,
  taxBreakdown: [],
  totalTaxCollected: 0,
};

const TaxReport: React.FC = () => {
  const defaultRange = getRangeByPreset("today");
  const [filters, setFilters] = useState<ReportFilters>(() => ({
    ...buildDefaultFilters(),
    datePreset: "today",
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  }));
  const [data, setData] = useState<TaxReportResponse>(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTaxReport = async () => {
      try {
        setLoading(true);
        const res = await api.get<TaxReportResponse>("/reports/taxes", {
          params: buildReportParams(filters),
        });
        setData(res.data);
      } catch (error) {
        console.error("Failed to fetch tax report", error);
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };

    fetchTaxReport();
  }, [filters]);

  // Calculate totals for the table
  const totalTaxableAmount = data.taxBreakdown.reduce(
    (sum, row) => sum + row.taxableAmount,
    0
  );
  const totalTaxableItemSales = data.taxBreakdown.reduce(
    (sum, row) => sum + row.taxableItemSales,
    0
  );
  const totalTaxableServiceCharges = data.taxBreakdown.reduce(
    (sum, row) => sum + row.taxableServiceCharges,
    0
  );

  const handleDownloadCsv = () => {
    const csv = buildCsvFromSections([
      {
        title: "Summary",
        headers: ["Metric", "Value"],
        rows: [
          ["Taxable Item Sales", currency(data.taxableItemSales)],
          ["Taxable Service Charges", currency(data.taxableServiceCharges)],
          ["Non-Taxable Item Sales", currency(data.nonTaxableItemSales)],
          [
            "Non-Taxable Service Charges",
            currency(data.nonTaxableServiceCharges),
          ],
        ],
      },
      {
        title: "Tax Breakdown",
        headers: [
          "Tax Name",
          "Taxable Amount",
          "Taxable Item Sales",
          "Taxable Service Charges",
          "Tax Collected",
        ],
        rows: [
          ...data.taxBreakdown.map((row) => [
            row.taxName,
            currency(row.taxableAmount),
            currency(row.taxableItemSales),
            currency(row.taxableServiceCharges),
            currency(row.taxCollected),
          ]),
          [
            "Total",
            currency(totalTaxableAmount),
            currency(totalTaxableItemSales),
            currency(totalTaxableServiceCharges),
            currency(data.totalTaxCollected),
          ],
        ],
      },
    ]);
    downloadCsv(
      `sales-taxes-report-${filters.startDate}-to-${filters.endDate}.csv`,
      csv,
    );
  };

  return (
    <div className="mx-auto h-screen max-w-7xl overflow-y-auto no-scrollbar p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white/90 py-6 backdrop-blur-sm">
        <h1 className="text-2xl font-bold text-gray-800">Sales Taxes</h1>
        <DownloadCsvButton onClick={handleDownloadCsv} disabled={loading} />
      </div>

      <ReportsFilters value={filters} onChange={setFilters} showOrderFilters={false} />

      {loading ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-2xl font-bold text-gray-900">
                {currency(data.taxableItemSales)}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Taxable Item Sales
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-2xl font-bold text-gray-900">
                {currency(data.taxableServiceCharges)}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Taxable Service Charges
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-2xl font-bold text-gray-900">
                {currency(data.nonTaxableItemSales)}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Non-Taxable Item Sales
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-2xl font-bold text-gray-900">
                {currency(data.nonTaxableServiceCharges)}
              </p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Non-Taxable Service Charges
              </p>
            </div>
          </div>

          {/* Tax Breakdown Table */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                  <tr className="border-b border-gray-200">
                    <th className="p-3 text-left font-semibold">Tax Name</th>
                    <th className="p-3 text-right font-semibold">Taxable Amount</th>
                    <th className="p-3 text-right font-semibold">Taxable Item Sales</th>
                    <th className="p-3 text-right font-semibold">Taxable Service Charges</th>
                    <th className="p-3 text-right font-semibold">Tax Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {data.taxBreakdown.map((row) => (
                    <tr
                      key={row.taxName}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      <td className="p-3 text-sm font-medium text-gray-700">
                        {row.taxName}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        {currency(row.taxableAmount)}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        {currency(row.taxableItemSales)}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        {currency(row.taxableServiceCharges)}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        {currency(row.taxCollected)}
                      </td>
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                    <td className="p-3 text-sm text-gray-900">Total</td>
                    <td className="p-3 text-right text-sm text-gray-900">
                      {currency(totalTaxableAmount)}
                    </td>
                    <td className="p-3 text-right text-sm text-gray-900">
                      {currency(totalTaxableItemSales)}
                    </td>
                    <td className="p-3 text-right text-sm text-gray-900">
                      {currency(totalTaxableServiceCharges)}
                    </td>
                    <td className="p-3 text-right text-sm text-gray-900">
                      {currency(data.totalTaxCollected)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaxReport;
